<script>
import date from 'locutus/php/datetime/date'
import defaults from 'lodash/defaults'
import {
  fetchSupportedAssetPlugins,
  fetchAssetPlugin,
  createAssetPlugin,
  updateAssetPlugin,
} from '@apis/admin/asset'
import find from 'lodash/find'
import fromPairs from 'lodash/fromPairs'
import map from 'lodash/map'

export default {
  props: {
    asset_plugin_seq: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      params: {},
      config: {},
      supported_plugins: [],
      loading: false,
    }
  },
  computed: {
    c_config_fields() {
      if (
        !this.params.plugin ||
        !this.params.plugin.length ||
        !this.supported_plugins.length
      )
        return []

      const { config_fields } = find(this.supported_plugins, {
        plugin: this.params.plugin,
      })

      return config_fields
    },
  },
  watch: {
    'params.plugin'(to) {
      if (!this.c_config_fields.length) {
        this.config = {}
      }

      this.config = fromPairs(
        map(this.c_config_fields, (field) => [
          field.name,
          this.config[field.name] || field.default_value || '',
        ])
      )
    },
  },
  created() {
    this.init()
  },
  methods: {
    init() {
      this.loading = true
      this.loadSupportedPlugins()
        .then(() => Promise.all([this.loadAssetPlugin()]))
        .catch((e) => this.$toasted.error(e.message))
        .finally(() => {
          this.loading = false
        })
    },
    async loadSupportedPlugins() {
      const { data } = await fetchSupportedAssetPlugins()

      this.supported_plugins = data
    },
    async loadAssetPlugin() {
      const { plugin_info } = await fetchAssetPlugin(this.asset_plugin_seq)
      this.params = defaults(plugin_info, {
        seq: null,
        title: '',
        plugin: '',
        config: '{}',
        start_block: null,
        allow_scan: 'N',
        regdate: null,
      })

      this.config = JSON.parse(this.params.config)
    },
    handleSubmit() {
      const createOrUpdate = this.params.seq
        ? updateAssetPlugin
        : createAssetPlugin
      const confirm_message = this.params.seq
        ? this.$t('admin.msg_save')
        : this.$t('admin.msg_enroll')
      const success_message = this.params.seq
        ? this.$t('admin.msg_plugin_modify')
        : this.$t('admin.msg_plugin_enroll')

      this.loading = true
      this.$confirm(confirm_message, this.$t('admin.modal_confirm'))
        .then(() =>
          createOrUpdate({
            ...this.params,
            config: JSON.stringify(this.config),
          })
        )
        .then(({ seq }) => this.$emit('submit', seq))
        .then(() => this.$toasted.success(success_message))
        .catch((e) => (e ? this.$toasted.error(e.message) : null))
        .finally(() => {
          this.loading = false
        })
    },
    date,
  },
}
</script>

<template>
  <form
    v-loading="loading"
    action="./"
    method="get"
    @submit.prevent="handleSubmit"
  >
    <table cellspacing="0" cellpadding="0" :class="$style.rowTable">
      <colgroup>
        <col width="20%" />
        <col width="30%" />
        <col width="20%" />
        <col width="30%" />
      </colgroup>
      <tbody>
        <tr>
          <th scope="row"
            ><div>{{ $t('admin.asset_plugin_seq') }}</div></th
          >
          <td>{{ params.seq || $t('admin.auto') }}</td>
          <th scope="row"
            ><div>{{ $t('admin.c2c_market_regdate') }}</div></th
          >
          <td>{{
            params.seq
              ? date('Y-m-d H:i:s', new Date(params.regdate))
              : $t('admin.before_enroll')
          }}</td>
        </tr>
        <tr>
          <th scope="row"
            ><div>{{ $t('admin.asset_plugin_name') }}</div></th
          >
          <td
            ><input
              v-if="!loading"
              v-model="params.title"
              :class="[$style.inputTypeText, $style.fullWidth]"
          /></td>
          <th scope="row"
            ><div>{{ $t('admin.asset_plugin') }}</div></th
          >
          <td>
            <template v-if="params.seq">{{ params.plugin }}</template>
            <select v-else v-model="params.plugin">
              <option value="">{{ $t('select') }}</option>
              <option
                v-for="{ plugin } in supported_plugins"
                :key="plugin"
                :value="plugin"
                >{{ plugin }}</option
              >
            </select>
          </td>
        </tr>
        <tr>
          <th scope="row"
            ><div>{{ $t('admin.asset_plugin_start_block') }}</div></th
          >
          <td
            ><input
              v-model="params.start_block"
              type="text"
              :class="[$style.inputTypeText, $style.w60, $style.tRight]"
          /></td>
          <th scope="row"
            ><div>{{ $t('admin.allow_scan') }}</div></th
          >
          <td
            ><ElSwitch
              v-model="params.allow_scan"
              active-value="Y"
              inactive-value="N"
            ></ElSwitch
          ></td>
        </tr>
        <tr v-for="field in c_config_fields" :key="field.name">
          <th scope="row"
            ><div>{{ field.title }}</div></th
          >
          <td colspan="3">
            <input
              v-if="field.field_type === 'INPUT'"
              v-model="config[field.name]"
              :class="[$style.inputTypeText, $style.fullWidth]"
            />
            <select
              v-if="field.field_type === 'SELECT'"
              v-model="config[field.name]"
            >
              <option
                v-for="option in field.options"
                :key="option.value"
                :value="option.value"
                >{{ option.title }}</option
              >
            </select>
          </td>
        </tr>
        <tr>
          <th colspan="4" :class="$style.button">
            <span :class="[$style.button, $style.black]"
              ><input
                type="submit"
                :value="params.seq ? $t('admin.save') : $t('admin.enroll')"
            /></span>
          </th>
        </tr>
      </tbody>
    </table>
  </form>
</template>

<style lang="scss" module>
@import '@src/design/default.scss';
@import '@src/design/admin.scss';
@import '@src/design/font.scss';
@import '@src/design/button.scss';
</style>
